import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('theme');
      return savedTheme === 'dark';
    }
    return false;
  });

  const [primaryColor, setPrimaryColor] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedColor = localStorage.getItem('primaryColor');
      return savedColor || '#A80A0A';
    }
    return '#A80A0A';
  });

  const [fontSize, setFontSize] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedFontSize = localStorage.getItem('fontSize');
      return savedFontSize || 'medium';
    }
    return 'medium';
  });

  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  useEffect(() => {
    localStorage.setItem('primaryColor', primaryColor);
  }, [primaryColor]);

  useEffect(() => {
    localStorage.setItem('fontSize', fontSize);
  }, [fontSize]);

  const toggleTheme = () => {
    setIsDarkMode((prev) => !prev);
  };

  const changePrimaryColor = (color) => {
    setPrimaryColor(color);
  };

  const changeFontSize = (size) => {
    setFontSize(size);
  };

  // Create dynamic theme based on current settings
  const theme = useMemo(() => {
    const baseFontSize = {
      small: 14,
      medium: 16,
      large: 18
    }[fontSize];

    return createTheme({
      palette: {
        mode: isDarkMode ? 'dark' : 'light',
        primary: {
          main: primaryColor,
          light: `${primaryColor}80`,
          dark: `${primaryColor}CC`,
          contrastText: '#FFFFFF',
        },
        background: {
          default: isDarkMode ? '#121212' : '#F8F9FA',
          paper: isDarkMode ? '#1E1E1E' : '#FFFFFF',
        },
        text: {
          primary: isDarkMode ? '#FFFFFF' : '#2C3E50',
          secondary: isDarkMode ? '#B0B0B0' : '#6C757D',
        },
        divider: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
      },
      typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        fontSize: baseFontSize,
        h1: { fontWeight: 700, fontSize: baseFontSize * 2.5 },
        h2: { fontWeight: 700, fontSize: baseFontSize * 2 },
        h3: { fontWeight: 600, fontSize: baseFontSize * 1.75 },
        h4: { fontWeight: 600, fontSize: baseFontSize * 1.5 },
        h5: { fontWeight: 600, fontSize: baseFontSize * 1.25 },
        h6: { fontWeight: 600, fontSize: baseFontSize * 1.1 },
        body1: { fontSize: baseFontSize },
        body2: { fontSize: baseFontSize * 0.875 },
      },
      shape: {
        borderRadius: 8,
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              fontWeight: 600,
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              boxShadow: '0 4px 24px rgba(0,0,0,0.05)',
            },
          },
        },
      },
    });
  }, [isDarkMode, primaryColor, fontSize]);

  return (
    <ThemeContext.Provider value={{ 
      isDarkMode, 
      toggleTheme, 
      primaryColor, 
      changePrimaryColor,
      fontSize,
      changeFontSize
    }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}; 